'use client';

import { useActionState, useCallback, useEffect, useId, useState } from 'react';
import styles from './flow.module.css';
import { FlowField } from './fields';
import { Loader } from '../strip';
import {
  activeSteps,
  initialValues,
  stepError,
  stepHelp,
  stepQuestion,
  submitValues,
  type FlowStep,
  type FlowValues,
} from './types';

/**
 * A field-by-field form.
 *
 * One question at a time, a Next button, Enter to continue, Escape to go back,
 * a review of every answer before anything is sent. See `types.ts` for why the
 * forms that matter on this platform are shaped this way.
 *
 * **The server action is still the only validation that counts.** Everything
 * here answers immediately so the person is not told at the end that the third
 * question was wrong; none of it is trusted. The action re-parses the whole
 * submission with Zod, and `state.error` from the action is shown on the review
 * screen where the person can go back and change any answer.
 *
 * Submission is a real `<form>` carrying hidden inputs, posted through
 * `useActionState`. That is deliberate: it means an existing server action that
 * reads `FormData` keeps working unchanged, and the flow is a presentation
 * choice rather than a second way of talking to the server.
 *
 * Three things a step can do beyond taking an answer, all added for the title
 * submission and all general:
 *
 *   - **`when`** decides whether a question is asked at all. The engine counts
 *     through the steps that apply rather than through every step declared, so
 *     a question that stops applying stops occupying a position in the progress
 *     strip, on the review screen and in what is submitted.
 *   - **`action`** is work a step does on its way past that answers *other*
 *     questions — pasting a Steam link and filling in the name, the address and
 *     the price. It runs from Next rather than from a button of its own, so the
 *     link step has one control and not two. It writes values and submits
 *     nothing; the review screen is still the only thing that sends anything.
 *   - **`preview`** renders something under a field that a sentence cannot
 *     carry. The Steam import's header image, in practice: a picture of the
 *     wrong game is the fastest way to see that the wrong link was pasted.
 *
 * And one kind of step rather than one thing a step can do, added for the
 * creator application (#91):
 *
 *   - **`connect`** takes an answer that cannot be typed, because only the
 *     platform can give it — an OAuth round trip proving somebody owns the
 *     channel they are claiming. Its button says what it does rather than
 *     `Next`, for the same one-control reason `action` runs from Next, and it
 *     writes several answers at once the way `action` does. See
 *     `FlowConnectStep`.
 */

export interface FlowActionState {
  error: string | null;
  ok: string | null;
}

/**
 * The screen shown before the first question.
 *
 * Opening straight onto "What is your studio called?" asks somebody to start
 * answering before they have been told what they are filling in, how long it
 * is, or what happens at the end. One screen of explanation and a button costs
 * a click and removes all three questions.
 */
export interface FlowWelcome {
  eyebrow?: string;
  title: string;
  /** A paragraph each. The first is set as the lead. */
  body: readonly string[];
  startLabel?: string;
}

/**
 * The default availability check: this origin's own `/api/availability`.
 *
 * A failed request resolves to "no objection" rather than blocking. The write
 * checks the same thing inside the transaction that creates the row, so this is
 * a courtesy; treating a network blip as "taken" would strand somebody on a
 * name that is actually free.
 */
async function defaultCheck(kind: string, value: string): Promise<string | null> {
  const response = await fetch(`/api/availability/${kind}?value=${encodeURIComponent(value)}`, {
    headers: { accept: 'application/json' },
  });
  if (!response.ok) return null;

  const body: unknown = await response.json();
  if (typeof body === 'object' && body !== null && 'taken' in body && body.taken === true) {
    return 'reason' in body && typeof body.reason === 'string'
      ? body.reason
      : 'That address is taken. Try another.';
  }
  return null;
}

/**
 * Back, and the key that does the same thing.
 *
 * One component for the two places it appears — above the question and under
 * the review — because they were two copies of a muted underlined word and
 * would have stayed two copies of whatever replaced it.
 *
 * The key is printed on the control rather than beside it: they are one fact,
 * and a shortcut nobody is told about is a shortcut nobody uses. `kbd` because
 * that is what `kbd` is, and it is what a screen reader announces as a key.
 */
function BackButton({ onClick }: { onClick: () => void }): React.ReactElement {
  return (
    <button type="button" className={styles.back} onClick={onClick}>
      Back
      <kbd className={styles.backKey}>Esc</kbd>
    </button>
  );
}

/**
 * What the one button on a step says.
 *
 * Four answers where there used to be three, and the new one is the reason this
 * is a function rather than a nested ternary in the middle of the controls row:
 * a connect step's button is not `Next`, it is `Connect Twitch`, because the
 * press *is* the answer rather than an acceptance of one. See
 * `FlowConnectStep`.
 *
 * Once a connect step has an answer it is an ordinary step and its button says
 * what every other step's does.
 */
function primaryLabel(step: FlowStep, values: FlowValues, running: boolean, last: boolean): string {
  if (step.kind === 'connect' && (values[step.name] ?? '').trim() === '') {
    return running ? step.busyLabel(values) : step.connectLabel(values);
  }
  if (running && step.kind === 'text' && step.action) return step.action.busyLabel;
  return last ? 'Review' : 'Next';
}

export function Flow({
  steps,
  action,
  submitLabel,
  secondaryLabel,
  reviewTitle = 'Check this over',
  reviewHelp,
  intro,
  welcome,
  checkAvailability,
  verifyAnswer,
}: {
  steps: readonly FlowStep[];
  action: (previous: FlowActionState, formData: FormData) => Promise<FlowActionState>;
  submitLabel: string;
  /**
   * A second way to finish, when finishing means two different things.
   *
   * The title submission is the case: the primary asks us to publish the game
   * and the secondary keeps it as a draft only the studio can see. Both send
   * the same answers to the same action, so this renders a second submit button
   * carrying `flowIntent=secondary` and the action decides — which is what a
   * submit button's name and value are for, and cheaper than a second action.
   *
   * Omit it and the review screen has one button, which is the ordinary case.
   */
  secondaryLabel?: string | undefined;
  reviewTitle?: string;
  reviewHelp?: string;
  /** Extra hidden fields the action needs that are not questions. */
  intro?: Readonly<Record<string, string>> | undefined;
  /** Shown before the first question. Omit to open straight onto the form. */
  welcome?: FlowWelcome | undefined;
  /**
   * How a `check` step asks whether an address is free.
   *
   * Returns the reason it is not, or null for no objection. The default asks
   * `/api/availability/:kind` on this origin, which is where it lived when this
   * engine served one Next application and both halves were the same
   * deployment.
   *
   * They are not any more. The React app is on its own origin and the endpoint
   * is `GET /v1/availability/:kind` on the API, behind a bearer token — so a
   * flow rendered there has to be handed a resolver that knows both of those
   * things. Without this the default fetch 404s, which `runCheck` reads as *no
   * objection*, and the step silently stops checking.
   */
  checkAvailability?: ((kind: string, value: string) => Promise<string | null>) | undefined;
  /**
   * How a `verify` step asks whether an answer is true.
   *
   * The same shape as `checkAvailability` and the same failure policy — returns
   * the problem, or null for no objection — and separate from it because what
   * is being asked is different in kind. `checkAvailability` asks our own
   * database a question we know the answer to. This asks somebody else's, and
   * "somebody else did not answer" has to mean *no objection* rather than *no*.
   *
   * There is no default. A flow with a `verify` step and no resolver simply
   * stops verifying, which is the same quiet failure the note on
   * `checkAvailability` describes and is swept for in the same test.
   */
  verifyAnswer?:
    | ((kind: string, value: string, context: Record<string, string>) => Promise<string | null>)
    | undefined;
}): React.ReactElement {
  const [state, formAction, pending] = useActionState(action, { error: null, ok: null });

  const [values, setValues] = useState<FlowValues>(() => initialValues(steps));
  /** Which steps have been typed in, so `derive` does not overwrite an answer. */
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [index, setIndex] = useState(0);
  /** False only while the welcome screen is up. No welcome, already started. */
  const [started, setStarted] = useState(welcome === undefined);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [goingBack, setGoingBack] = useState(false);

  const headingId = useId();

  /*
   * The questions actually being asked, which is not always every question.
   *
   * Everything below counts through this rather than through `steps`: the
   * progress strip, the index, the review list. A step whose `when` has gone
   * false must stop occupying a position immediately, or Back walks into a
   * question that is no longer being asked and Next skips one that is.
   */
  const asked = activeSteps(steps, values);

  const onReview = index >= asked.length;
  const step = onReview ? null : asked[index]!;

  /**
   * Fill in a derived answer when a step is reached untouched.
   *
   * Runs on arrival rather than on every keystroke of the source field, so
   * going back and renaming the studio updates the proposed address, while an
   * address that was typed deliberately survives.
   */
  useEffect(() => {
    if (!step?.derive || touched.has(step.name)) return;
    const suggested = step.derive(values);
    setValues((current) =>
      current[step.name] === suggested ? current : { ...current, [step.name]: suggested },
    );
    // `values` is intentionally not a dependency: this should fire when the
    // step changes, not whenever any answer changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, step, touched]);

  const setValue = useCallback((name: string, value: string) => {
    setValues((current) => ({ ...current, [name]: value }));
    setTouched((current) => (current.has(name) ? current : new Set(current).add(name)));
    setError(null);
  }, []);

  /**
   * Whether a slug is free, asked of the server.
   *
   * A uniqueness check cannot be done in the browser, and finding out that the
   * address is taken after filling in five more fields is exactly the ending
   * this shape of form exists to avoid.
   *
   * A failed request resolves to "no objection" rather than blocking. The
   * server action checks the same thing inside the transaction that creates the
   * row, so this is a courtesy; treating a network blip as "taken" would strand
   * somebody on a name that is actually free.
   */
  const runCheck = useCallback(
    async (kind: string, value: string): Promise<string | null> => {
      const ask = checkAvailability ?? defaultCheck;
      try {
        return await ask(kind, value);
      } catch {
        return null;
      }
    },
    [checkAvailability],
  );

  /**
   * Whether an answer is true, asked of whoever would know.
   *
   * Failure is *no objection*, exactly as above and for a stronger reason: the
   * thing on the other end is a foreign government's web service, and treating
   * its bad afternoon as a refusal would be refusing a real company its own
   * registration number. The server it calls applies the same policy one layer
   * down — see `lookupCompanyNumber`, which resolves everything except an
   * explicit *no such company* to "nothing established".
   */
  const runVerify = useCallback(
    async (
      verify: NonNullable<FlowStep['verify']>,
      value: string,
      answers: FlowValues,
    ): Promise<string | null> => {
      if (!verifyAnswer) return null;
      try {
        return await verifyAnswer(verify.kind, value, verify.context?.(answers) ?? {});
      } catch {
        return null;
      }
    },
    [verifyAnswer],
  );

  /**
   * Move on, judging the answers as they will be rather than as they were.
   *
   * `answers` is the parameter that makes picking a card work. A card answers
   * and continues in one act, so `onPick` writes the value and calls this — and
   * a `useCallback` closed over `values` still holds the render *before* the
   * pick, in which the step is unanswered. So the click that chose `Yes` was
   * judged against no answer at all, `stepError` returned `This one is needed
   * to continue`, and the flow sat on the question it had just been given the
   * answer to. Every card step in the product did this: the studio picker, the
   * price ladder, the creator rate, and the Steam question the complaint came
   * in about.
   *
   * A `setTimeout(…, 0)` used to stand where the parameter does. It cannot have
   * worked: waiting a task lets React re-render, but the closure this function
   * came out of is fixed at the moment of the click and no amount of waiting
   * replaces it.
   *
   * Typing does not go through here — a keystroke is a draft and `advance` is
   * reached from Enter or from Next, both of which happen a render later with
   * the value already in `values`.
   */
  const advance = useCallback(
    async (answers: FlowValues = values) => {
      if (!step || checking) return;

      /*
       * A connect step supplies its own answer, so it runs before the check
       * that there is one.
       *
       * The button on this screen says `Connect Twitch` rather than `Next` —
       * see `FlowConnectStep` — and pressing it is how the answer arrives. Left
       * below `stepError` it would be refused with `This one is needed to
       * continue`, which is a true sentence about a state the button exists to
       * leave.
       *
       * Only while it is unanswered. Once it has worked the step behaves like
       * any other and the button is Next again; redoing it is the quieter
       * control beside it, which clears the value first.
       */
      if (step.kind === 'connect' && (answers[step.name] ?? '').trim() === '') {
        setRunning(true);
        setError(null);
        try {
          const filled = await step.connect(answers);
          setValues((current) => ({ ...current, ...filled }));
          setTouched((current) => {
            const next = new Set(current);
            for (const name of Object.keys(filled)) next.add(name);
            return next;
          });

          // Nothing to move on to if the connect came back with no answer for
          // its own step: that is the shape of "the popup closed on its own",
          // and the honest response is to leave the button where it was.
          if ((filled[step.name] ?? '').trim() === '') return;
        } catch (caught) {
          setError(caught instanceof Error ? caught.message : 'That did not work. Try again.');
          return;
        } finally {
          setRunning(false);
        }

        setGoingBack(false);
        setIndex((current) => current + 1);
        return;
      }

      const value = answers[step.name] ?? '';
      const problem = stepError(step, value, answers);
      if (problem) {
        setError(problem);
        return;
      }

      if (step.check && value.trim() !== '') {
        setChecking(true);
        const taken = await runCheck(step.check, value.trim());
        setChecking(false);
        if (taken) {
          setError(taken);
          return;
        }
      }

      /*
       * And whether it is true, asked of somebody outside this product.
       *
       * After `stepError`, so a number of the wrong shape is refused by the
       * offline rule that already knows — no round trip to be told what a
       * regular expression could say instantly, and no register asked about a
       * string that cannot be one of its numbers.
       */
      if (step.verify && value.trim() !== '') {
        setChecking(true);
        const problem = await runVerify(step.verify, value.trim(), answers);
        setChecking(false);
        if (problem) {
          setError(problem);
          return;
        }
      }

      /*
       * The step's own work, done on the way past rather than from a button
       * beside the field. See `FlowAction`: the Steam link had `Fill from
       * Steam` next to Next, two controls under one answer with nothing to say
       * which was which, and pressing the wrong one carried an empty name and
       * an empty price into the next three questions.
       *
       * A failure stops the step and says why. The sentence that comes back
       * names which of three things went wrong — the link is not Steam's, Steam
       * has no such page, or Steam did not answer — and only the first is worth
       * editing the box over, so it is shown under the box.
       *
       * Everything it writes is marked touched, so `derive` will not later
       * overwrite it: an address proposed from a name Steam gave us is still an
       * address somebody has now seen.
       */
      if (step.kind === 'text' && step.action) {
        setRunning(true);
        try {
          const filled = await step.action.run(value.trim());
          setValues((current) => ({ ...current, ...filled }));
          setTouched((current) => {
            const next = new Set(current);
            for (const name of Object.keys(filled)) next.add(name);
            return next;
          });
        } catch (caught) {
          setError(caught instanceof Error ? caught.message : 'That did not work. Try again.');
          return;
        } finally {
          setRunning(false);
        }
      }

      setError(null);
      setGoingBack(false);
      setIndex((current) => current + 1);
    },
    [checking, runCheck, runVerify, step, values],
  );

  const goBack = useCallback(() => {
    setError(null);
    setGoingBack(true);
    setIndex((current) => Math.max(0, current - 1));
  }, []);

  /**
   * The keyboard: Enter continues, Escape goes back.
   *
   * This shape of form is a keyboard form — the field takes focus on arrival,
   * Enter continues, and the whole thing can be filled in without reaching for
   * the mouse. Every step prints `Press Enter ↵` under its button saying so.
   *
   * **On half the steps that was not true.** Enter was handled by the text
   * field and nowhere else, so it worked on a text question and did nothing at
   * all on a card one — where there is no input, nothing takes focus on
   * arrival, and the keystroke reached no handler. Three of the submission
   * flow's six questions are cards, so somebody filling it in from the keyboard
   * hit a screen that ignored the key it was telling them to press, twice.
   * Escape has always been on `window` for exactly this reason and the note
   * below it named the case; Enter never followed.
   *
   * Escape rather than Backspace or an arrow for going back. Backspace is a
   * character in every field on screen and Left is a cursor move in the same
   * fields; Escape is the one key that already means *out of this, back to
   * where I was* and means nothing to a text input.
   *
   * Four things take their own Enter and this must not act on top of them:
   *
   *   A field that handled it. Both text fields and the filtered list call
   *   `preventDefault`, so `defaultPrevented` is the flag that says so.
   *
   *   A focused button or link. The browser turns Enter on a button into a
   *   click, so a card reached by Tab already answers its question — and
   *   advancing here as well would answer it and then skip the next one.
   *
   *   A textarea, where Enter is a newline. Ctrl or Cmd with it continues, and
   *   the field handles that itself and stops it here.
   *
   *   An IME. Enter commits a candidate in Japanese, Chinese and Korean input,
   *   and `isComposing` is how that is told apart from an answer. Without it,
   *   choosing a character submits the question.
   *
   * Refused while something is in flight: an availability lookup and a Steam
   * fetch both write to the step they were started from, and stepping off it
   * mid-request would land the answer on a question that is no longer on
   * screen. Refused while the submission is pending for the same reason.
   *
   * **The review screen is deliberately not included.** It has two endings and
   * no `Press Enter` on it, and picking one of them with a keystroke nobody was
   * offered is not a shortcut — it is a submission somebody meant to re-read.
   */
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented) return;
      if (event.isComposing || event.keyCode === 229) return;
      if (checking || running || pending) return;

      if (event.key === 'Escape') {
        if (!started || index === 0) return;
        event.preventDefault();
        goBack();
        return;
      }

      // Shift and Alt with Enter are somebody asking for something else.
      if (event.key !== 'Enter' || event.shiftKey || event.altKey) return;

      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('button, a[href], summary, select')) return;
      if (target?.tagName === 'TEXTAREA') return;

      event.preventDefault();

      // The door. `Start` is the only control on it, so Enter is what it means.
      if (!started) {
        setStarted(true);
        return;
      }

      if (onReview) return;

      // Not gated on the step being answered: `advance` runs the same check the
      // button does and puts the sentence on screen, which is the explanation a
      // disabled Next has nowhere to put.
      void advance();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [started, index, checking, running, pending, goBack, advance, onReview]);

  const editStep = useCallback((target: number) => {
    setError(null);
    setGoingBack(true);
    setIndex(target);
  }, []);

  const skip = useCallback(() => {
    if (!step) return;
    setValues((current) => ({ ...current, [step.name]: '' }));
    setTouched((current) => new Set(current).add(step.name));
    setError(null);
    setGoingBack(false);
    setIndex((current) => current + 1);
  }, [step]);

  /*
   * The welcome screen.
   *
   * Ahead of the success check on purpose: `started` can only be false before
   * anything has been submitted, so the two can never both be true, and putting
   * it first keeps the "nothing else on this screen is still true" rule below
   * as the last word on what renders.
   *
   * No progress strip here. The strip means "this got further", and standing at
   * the door having answered nothing is not further.
   */
  if (welcome && !started) {
    return (
      <div className={`${styles.flow} ${styles.welcome}`}>
        <div className={styles.step}>
          {welcome.eyebrow && <p className="gb-eyebrow">{welcome.eyebrow}</p>}
          <h1 className={styles.question}>{welcome.title}</h1>
          {welcome.body.map((paragraph, position) => (
            <p
              key={paragraph}
              className={position === 0 ? 'gb-lead' : `gb-body ${styles.help}`}
              style={position === 0 ? undefined : { margin: 0 }}
            >
              {paragraph}
            </p>
          ))}

          <div className={styles.controls}>
            <button type="button" className={styles.next} onClick={() => setStarted(true)}>
              {welcome.startLabel ?? 'Get started'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // The action reported success. Nothing else on this screen is still true.
  if (state.ok !== null) {
    return (
      <div className={styles.done}>
        <p className="gb-lead" role="status">
          {state.ok}
        </p>
      </div>
    );
  }

  const answered = submitValues(steps, values);

  /*
   * Whether the question on screen is still unanswered.
   *
   * Next is switched off rather than left live to answer "This one is needed to
   * continue" a click later. The click was doing nothing either way; the
   * difference is that a dead button says so before it is pressed, which is the
   * whole of the complaint — somebody arriving at the submission flow, pressing
   * Start and then pressing Next got a screen that refused to move with no
   * indication it was ever going to.
   *
   * An optional step is never blocked: an empty answer is a real answer there,
   * and it has its own skip control saying so. Enter still runs the full check
   * and still reports the sentence, so the keyboard path keeps the explanation
   * this removes from the button.
   */
  const unanswered =
    step !== null &&
    step.optional !== true &&
    // A connect step's button is what produces the answer, so an empty value is
    // the state it exists to act on rather than a reason to switch it off.
    step.kind !== 'connect' &&
    (values[step.name] ?? '').trim() === '';

  return (
    <div className={styles.flow}>
      <div className={styles.progressHead}>
        <span className={styles.progressCount}>
          {onReview ? 'Review' : `${index + 1} of ${asked.length}`}
        </span>
        {index > 0 && <BackButton onClick={goBack} />}
      </div>

      {/*
        The progress strip, which is the guidelines' signature device doing the
        job it already means. A percentage bar would have been the one place in
        the product where progress is drawn in a way that means nothing.

        It is also the way back to any question behind you. It already showed
        which one you were on and how many were done, and pointing at the third
        of seven did nothing — so returning to it was Back three times, or the
        whole way forward to the review screen, where every answer finally has a
        `Change` beside it. The information was on screen and the act it implies
        was not wired up.

        Forward is not offered. Skipping ahead would walk past a required answer
        and land on a question whose Next is dead for a reason two screens back,
        and there is nothing on a bar that could explain that. `editStep` is the
        same jump the review screen's `Change` makes.

        No `aria-hidden` any more: it was a decoration and is a navigation, and
        a hidden container full of focusable buttons is the worst of both.
      */}
      <div className={styles.progress} role="group" aria-label="Questions in this form">
        {asked.map((entry, position) => {
          const state = position < index ? 'done' : position === index ? 'current' : 'todo';
          // In flight, a jump would land the answer on a question that is no
          // longer on screen — the same reason Escape is refused. See `goBack`.
          const reachable = state === 'done' && !checking && !running && !pending;

          return (
            <button
              key={entry.name}
              type="button"
              className={styles.progressStep}
              disabled={!reachable}
              title={stepQuestion(entry, values)}
              aria-label={`${position + 1} of ${asked.length}: ${stepQuestion(entry, values)}`}
              {...(state === 'current' ? { 'aria-current': 'step' as const } : {})}
              onClick={() => editStep(position)}
            >
              <i className={styles.progressBar} data-state={state} />
            </button>
          );
        })}
      </div>

      {step ? (
        <div
          key={step.name}
          className={`${styles.step} ${goingBack ? styles.stepEnterBack : styles.stepEnter}`}
        >
          <h2 className={styles.question} id={headingId}>
            {stepQuestion(step, values)}
          </h2>
          {(() => {
            const help = stepHelp(step, values);
            return help ? <p className={`gb-body ${styles.help}`}>{help}</p> : null;
          })()}

          <FlowField
            step={step}
            value={values[step.name] ?? ''}
            values={values}
            invalid={error !== null}
            describedBy={headingId}
            onChange={(value) => setValue(step.name, value)}
            onEnter={() => void advance()}
            onPick={(value) => {
              setValue(step.name, value);

              /*
                A list or a card is a decision, not a draft, so picking one
                moves on. Typing does not. The answers are handed over rather
                than read back out of state, which is one render too late — see
                `advance`.

                Unless the step says to stay, which one does. See `stay` in
                `types.ts`: what the creator rate *means* is four figures under
                the cards, and jumping showed them and took them away in the
                same instant.
              */
              if (step.kind === 'cards' && step.stay === true) return;
              void advance({ ...values, [step.name]: value });
            }}
          />

          {(() => {
            const hint = step.hint?.(values[step.name] ?? '', values) ?? null;
            return hint ? <p className={styles.hint}>{hint}</p> : null;
          })()}

          {step.preview?.(values) ?? null}

          {error !== null && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
          {checking && <Loader size="sm" label="Checking" />}
          {running && step.kind === 'text' && step.action && (
            <Loader size="sm" label={step.action.busyLabel} />
          )}
          {running && step.kind === 'connect' && (
            <Loader size="sm" label={step.busyLabel(values)} />
          )}

          <div className={styles.controls}>
            <button
              type="button"
              className={styles.next}
              onClick={() => void advance()}
              disabled={checking || running || unanswered}
            >
              {primaryLabel(step, values, running, index === asked.length - 1)}
            </button>
            {/*
              Doing it again, once it has been done.

              Somebody who connected the wrong account of two has no other way
              back: the answer is not typed, so there is nothing to clear, and
              Back would only take them to the question before this one. The
              value is emptied and the connect run in one act, which is what
              `advance` does for an unanswered connect step.
            */}
            {step.kind === 'connect' && (values[step.name] ?? '').trim() !== '' && (
              <button
                type="button"
                className={styles.ghost}
                disabled={running}
                onClick={() => {
                  const cleared = { ...values, [step.name]: '' };
                  setValues(cleared);
                  void advance(cleared);
                }}
              >
                {step.againLabel ?? 'Connect a different account'}
              </button>
            )}
            {step.optional && (values[step.name] ?? '').trim() === '' && (
              <button type="button" className={styles.ghost} onClick={skip}>
                {step.skipLabel ?? 'Skip this'}
              </button>
            )}
            <span className={styles.enterHint}>Press Enter ↵</span>
          </div>
        </div>
      ) : (
        <form action={formAction} className={styles.review}>
          <h2 className={styles.question}>{reviewTitle}</h2>
          {reviewHelp && <p className={`gb-body ${styles.help}`}>{reviewHelp}</p>}

          <ul className={styles.reviewList}>
            {asked.map((entry, position) => {
              const raw = values[entry.name] ?? '';
              const shown = raw.trim() === '' ? null : (entry.display?.(raw, values) ?? raw);
              return (
                <li key={entry.name} className={styles.reviewRow}>
                  <span className={styles.reviewLabel}>{stepQuestion(entry, values)}</span>
                  <span className={styles.reviewValue}>
                    {shown ?? <em className={styles.reviewEmpty}>Not given</em>}
                  </span>
                  <button type="button" className={styles.ghost} onClick={() => editStep(position)}>
                    Change
                  </button>
                </li>
              );
            })}
          </ul>

          {/* What the action actually receives, after every `submit` transform. */}
          {Object.entries(intro ?? {}).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} readOnly />
          ))}
          {steps.map((entry) => (
            <input
              key={entry.name}
              type="hidden"
              name={entry.name}
              value={answered[entry.name] ?? ''}
              readOnly
            />
          ))}

          {state.error !== null && (
            <p className={styles.error} role="alert">
              {state.error}
            </p>
          )}

          <div className={styles.controls}>
            <button type="submit" className={styles.next} disabled={pending}>
              {pending ? 'Sending…' : submitLabel}
            </button>
            {secondaryLabel && (
              /*
                A submit button's own name and value reach the action in the
                FormData, so the two endings are one form and one action rather
                than a second copy of the submission path.
              */
              <button
                type="submit"
                name="flowIntent"
                value="secondary"
                className={styles.ghost}
                disabled={pending}
              >
                {secondaryLabel}
              </button>
            )}
            <BackButton onClick={goBack} />
          </div>
        </form>
      )}
    </div>
  );
}
